import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { MaintainService } from '../../../components/views/maintain/maintain.service';
import { EncodingUtils } from '../../../common/encoding-utils';
import { PageResponseDTO } from '../../../common/paging-dto';
import { QueryOperand } from '../../../common/kibana-request-dto';
import { AdminContextService } from '../../../common/services/admin.context.service';

@Component({
  selector: 'app-find-user-details',
  templateUrl: './find-user-details.component.html',
  styleUrls: ['./find-user-details.component.scss'],
  standalone: false,
})
export class FindUserDetailsComponent implements OnInit {
  @Input('newIcon') newIcon: boolean;

  public findUserDetailsOn = false;

  public form: UntypedFormGroup;

  public searchEnabled: boolean;

  public message: string;

  public list: any[];

  public showMore = true;

  public cohortIcon: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private maintainService: MaintainService,
    private adminContextService: AdminContextService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group({
      id: [''],
      cohortId: [''],
      email: [''],
      firstName: [''],
      lastName: [''],
      phoneNumber: ['', Validators.minLength(4)],
    });

    this.form.valueChanges.subscribe((v) => {
      this.searchEnabled = Object.keys(v).filter((k) => !!v[k]).length > 0;
    });
  }

  clear() {
    this.form.setValue({
      email: '',
      id: '',
      cohortId: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
    });
    this.message = 'Form cleared';
    this.list = null;
  }

  search() {
    this.maintainService
      .performKibanaQuery({
        perPage: 50,
        offset: 0,
        index: 'lm-player-account',
        indexClass: 'lm.model.player.PlayerESO',
        convertTo: 'lm.admin.api.player.dtos.UserNinjaResultDTO',
        queryConditions: {
          index: 'lm-player-account',
          conditions: Object.keys(this.form.value)
            .filter((k) => !!this.form.value[k])
            .map((k) => ({
              fieldName: k == 'phoneNumber' ? 'phone-number.number' : EncodingUtils.encodeRequest(k),
              operand: QueryOperand.QUERY_STRING,
              value: this.form.value[k].trim().toLowerCase().replace('+', '*'),
            })),
        },
        orderParamTieBreaker: 'id',
      })
      .subscribe((resp) => this.handleResponse(resp));
  }

  private handleResponse(resp: PageResponseDTO<any>) {
    this.message = this.determineMessage(resp);
    const { content } = resp;
    if (content.length > 0) {
      const cohortId = content[0].cohortId || 1;
      this.form.setValue({
        email: content[0].email,
        id: content[0].id,
        cohortId,
        firstName: content[0].firstName,
        lastName: content[0].lastName,
        phoneNumber: content[0].mobileNumber,
      });
      this.cohortIcon = this.getIcon(cohortId);
    }

    if (content.length > 1) {
      this.list = resp.content;
    }
  }

  private determineMessage(resp: PageResponseDTO<any>) {
    return `Returned ${resp.total} results`;
  }

  public setElement(index: number) {
    this.form.setValue({
      email: this.list[index].email,
      id: this.list[index].id,
      cohortId: this.list[index].cohortId || 1,
      firstName: this.list[index].firstName,
      lastName: this.list[index].lastName,
      phoneNumber: this.list[index].mobileNumber,
    });
    this.cohortIcon = this.getIcon(this.list[index].cohortId);
  }

  public clearOne(formControlName: string) {
    this.form.get(formControlName).setValue('');
  }

  public copyOne(formControlName: string) {
    const el: any = document.createElement('textarea');
    el.value = this.form.get(formControlName).value;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  goTo(): void {
    const { id } = this.form.getRawValue();
    if (id) {
      const extras: NavigationExtras = { queryParams: { cohortId: 1 }, relativeTo: this.activatedRoute };
      this.router.navigate([`/manage/players/${id}/details`], extras);
      this.findUserDetailsOn = false;
    }
  }

  public getIcon(cohortId: number): string {
    return this.adminContextService.getCohortIcon(cohortId || 1);
  }
}
